import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const flowA = keyframes`
  0%, 100% { transform: translateX(-15%); }
  50%      { transform: translateX(15%); }
`;

const flowB = keyframes`
  0%, 100% { transform: translateX(10%); }
  50%      { transform: translateX(-10%); }
`;

const Wrapper = styled(Box)({ marginBottom: 28 });

const BarContainer = styled(Box)({
  height: 14,
  borderRadius: 7,
  background: 'rgba(0,0,0,0.06)',
  overflow: 'hidden',
  position: 'relative',
});

const Fill = styled(Box)({
  position: 'relative',
  height: '100%',
  borderRadius: 7,
  overflow: 'hidden',
  transition: 'width 0.6s cubic-bezier(.4,0,.2,1)',
  background: '#2ec4b6',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    inset: '-50% -25%',
    borderRadius: 'inherit',
  },
  '&::before': {
    background:
      'radial-gradient(ellipse 60% 200% at 20% 50%, #ffe066 0%, rgba(255,224,102,0) 60%), ' +
      'radial-gradient(ellipse 70% 200% at 75% 50%, #2ec4b6 0%, rgba(46,196,182,0) 60%)',
    backgroundSize: '200% 100%',
    mixBlendMode: 'screen',
    animation: `${flowA} 7s cubic-bezier(.45,.05,.55,.95) infinite`,
  },
  '&::after': {
    background:
      'radial-gradient(ellipse 70% 200% at 60% 50%, #ffe066 0%, rgba(255,224,102,0) 65%), ' +
      'radial-gradient(ellipse 80% 200% at 30% 50%, #1a7a72 0%, rgba(26,122,114,0) 70%)',
    backgroundSize: '220% 100%',
    opacity: 0.85,
    mixBlendMode: 'lighten',
    animation: `${flowB} 11s cubic-bezier(.45,.05,.55,.95) infinite reverse`,
  },
});

const CaptionRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: 10,
});

const TimeCaption = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.72rem',
  letterSpacing: '0.28em',
  color: '#888',
  textTransform: 'uppercase',
  fontWeight: 700,
});

const TimeValue = styled('span')({
  color: '#1a7a72',
  marginLeft: 6,
  letterSpacing: '0.12em',
});

const ProgressBar = ({ progress, formattedElapsed, formattedRemaining }) => {
  const [displayProgress, setDisplayProgress] = useState(progress);
  const animFrameRef = useRef(null);

  useEffect(() => {
    const animate = () => {
      setDisplayProgress((prev) => {
        const diff = progress - prev;
        if (Math.abs(diff) < 0.5) return progress;
        animFrameRef.current = requestAnimationFrame(animate);
        return prev + diff * 0.1;
      });
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => animFrameRef.current && cancelAnimationFrame(animFrameRef.current);
  }, [progress]);

  const clamped = Math.min(Math.max(displayProgress, 0), 100);

  return (
    <Wrapper>
      <BarContainer>
        <Fill data-progress-fill style={{ width: `${clamped}%` }} />
      </BarContainer>
      <CaptionRow>
        <TimeCaption>Elapsed<TimeValue>{formattedElapsed}</TimeValue></TimeCaption>
        <TimeCaption>Remaining<TimeValue>{formattedRemaining}</TimeValue></TimeCaption>
      </CaptionRow>
    </Wrapper>
  );
};

export default ProgressBar;
